import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { collection, getDocs, doc, updateDoc, arrayRemove } from 'firebase/firestore';
import { db } from '../../../firebaseConfig';
import ConfirmationModal from '../../../components/ConfirmationModal';
import './index.css';

const Bookings = ({ cancellations, setCancellations, memberId }) => {
  const [activeTab, setActiveTab] = useState('upcoming');
  const [bookings, setBookings] = useState([]);
  const [cancelBooking, setCancelBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchBookings = async () => {
      if (!memberId) {
        setError('User not authenticated');
        setLoading(false);
        return;
      }

      try {
        const bookingsCollection = collection(db, 'bookings');
        const bookingSnapshot = await getDocs(bookingsCollection);
        const bookingList = bookingSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
        })).filter(booking => booking.memberId === memberId);
        setBookings(bookingList);
        setLoading(false);
      } catch (err) {
        setError('Failed to fetch bookings');
        setLoading(false);
      }
    };
    fetchBookings();
  }, [memberId]);

  const handleCancel = (booking) => {
    setCancelBooking(booking);
  };

  const confirmCancel = async () => {
    if (!cancelBooking) return;

    try {
      const bookingRef = doc(db, 'bookings', cancelBooking.id);
      await updateDoc(bookingRef, {
        status: 'cancelled',
        cancelledAt: new Date(),
      });
      const villaRef = doc(db, 'villas', cancelBooking.villaId);
      const dateRange = [];
      let currentDate = new Date(cancelBooking.startDate);
      const endDate = new Date(cancelBooking.endDate);
      while (currentDate <= endDate) {
        dateRange.push(currentDate.toISOString().split('T')[0]);
        currentDate.setDate(currentDate.getDate() + 1);
      }
      await updateDoc(villaRef, {
        blockedDates: arrayRemove(...dateRange),
      });
      setCancellations((prev) => [...prev, { ...cancelBooking, status: 'cancelled' }]);
      setBookings((prev) =>
        prev.map((b) =>
          b.id === cancelBooking.id ? { ...b, status: 'cancelled' } : b
        )
      );
      setCancelBooking(null);
    } catch (err) {
      setError('Failed to cancel booking');
      console.error(err);
    }
  };

  const renderBookingList = (filteredBookings, showCancelButton = false) => {
    if (!filteredBookings || filteredBookings.length === 0) {
      return <p className="no-bookings">No bookings found</p>;
    }

    return (
      <ul className="booking-list">
        {filteredBookings.map((booking) => (
          <li key={booking.id} className="booking-item">
            <div className="booking-details">
              <h6 className="villa-name">{booking.villaName}</h6>
              <p className="booking-dates">
                {new Date(booking.startDate).toLocaleDateString()} -{' '}
                {new Date(booking.endDate).toLocaleDateString()}
              </p>
              <p className="booking-status">
                Status: {booking.status}
              </p>
            </div>
            {showCancelButton && booking.status === 'active' && (
              <button
                className="btn btn-cancel"
                onClick={() => handleCancel(booking)}
              >
                Cancel
              </button>
            )}
          </li>
        ))}
      </ul>
    );
  };

  if (loading) return <div className="spinner-container">
        <div className="spinner"></div>
    </div>;
  if (error) return <div>{error}</div>;

  return (
    <div className="bookings-container">
      <h2 className="section-title">My Bookings</h2>
      <div className="tabs">
        <button
          className={`tab ${activeTab === 'upcoming' ? 'active' : ''}`}
          onClick={() => setActiveTab('upcoming')}
        >
          Upcoming
        </button>
        <button
          className={`tab ${activeTab === 'past' ? 'active' : ''}`}
          onClick={() => setActiveTab('past')}
        >
          Past
        </button>
        <button
          className={`tab ${activeTab === 'cancellations' ? 'active' : ''}`}
          onClick={() => setActiveTab('cancellations')}
        >
          Cancellations
        </button>
      </div>
      <div className="tab-content">
        {activeTab === 'upcoming' && (
          <div className="booking-section">
            <h5 className="section-subtitle">Upcoming Bookings</h5>
            {renderBookingList(
              bookings.filter((b) => new Date(b.startDate) > new Date() && b.status === 'active'),
              true
            )}
          </div>
        )}
        {activeTab === 'past' && (
          <div className="booking-section">
            <h5 className="section-subtitle">Past Bookings</h5>
            {renderBookingList(
              bookings.filter((b) => new Date(b.endDate) <= new Date() && b.status === 'active')
            )}
          </div>
        )}
        {activeTab === 'cancellations' && (
          <div className="booking-section">
            <h5 className="section-subtitle">Cancelled Bookings</h5>
            {renderBookingList(
              bookings.filter((b) => b.status === 'cancelled')
            )}
          </div>
        )}
      </div>
      <ConfirmationModal
        show={!!cancelBooking}
        onClose={() => setCancelBooking(null)}
        onConfirm={confirmCancel}
        title="Confirm Cancellation"
        message={`Are you sure you want to cancel your booking for ${cancelBooking?.villaName} from ${cancelBooking?.startDate ? new Date(cancelBooking.startDate).toLocaleDateString() : ''} to ${cancelBooking?.endDate ? new Date(cancelBooking.endDate).toLocaleDateString() : ''}?`}
      />
    </div>
  );
};

Bookings.propTypes = {
  cancellations: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      villaId: PropTypes.string,
      villaName: PropTypes.string,
      startDate: PropTypes.string,
      endDate: PropTypes.string,
      memberId: PropTypes.string,
      basePricePerNight: PropTypes.string,
      additionalGuestCost: PropTypes.string,
      securityDeposit: PropTypes.string,
      status: PropTypes.string,
      timestamp: PropTypes.instanceOf(Date),
    })
  ),
  setCancellations: PropTypes.func.isRequired,
  memberId: PropTypes.string.isRequired,
};

Bookings.defaultProps = {
  cancellations: [],
};

export default Bookings;